import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { StorefrontLayout } from './layouts/StorefrontLayout';
import { AdminLayout } from './admin/layouts/AdminLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { PageLoader } from './components/PageLoader';

// Storefront pages
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const About = lazy(() => import('./pages/About'));
const WhyOrenyva = lazy(() => import('./pages/WhyOrenyva'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));
const Reviews = lazy(() => import('./pages/Reviews'));
const CartPage = lazy(() => import('./pages/CartPage'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AccountDashboard = lazy(() => import('./pages/account/AccountDashboard'));
const AccountOrders = lazy(() => import('./pages/account/AccountOrders'));
const AccountOrderDetail = lazy(() => import('./pages/account/AccountOrderDetail'));
const OrderTracking = lazy(() => import('./pages/account/OrderTracking'));
const AccountProfile = lazy(() => import('./pages/account/AccountProfile'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin pages
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const Dashboard = lazy(() => import('./admin/pages/Dashboard'));
const AdminOrders = lazy(() => import('./admin/pages/Orders'));
const AdminOrderDetail = lazy(() => import('./admin/pages/OrderDetail'));
const AdminShipments = lazy(() => import('./admin/pages/Shipments'));
const AdminProducts = lazy(() => import('./admin/pages/Products'));
const AdminProductEditor = lazy(() => import('./admin/pages/ProductEditor'));
const AdminInventory = lazy(() => import('./admin/pages/Inventory'));
const AdminCustomers = lazy(() => import('./admin/pages/Customers'));
const AdminCustomerDetail = lazy(() => import('./admin/pages/CustomerDetail'));
const AdminReviews = lazy(() => import('./admin/pages/Reviews'));
const AdminCoupons = lazy(() => import('./admin/pages/Coupons'));
const AdminCollections = lazy(() => import('./admin/pages/Collections'));
const AdminContent = lazy(() => import('./admin/pages/Content'));
const AdminMedia = lazy(() => import('./admin/pages/Media'));
const AdminAnalytics = lazy(() => import('./admin/pages/Analytics'));
const AdminSettings = lazy(() => import('./admin/pages/Settings'));
const AdminUsers = lazy(() => import('./admin/pages/AdminUsers'));
const AdminActivity = lazy(() => import('./admin/pages/Activity'));
const AdminClaims = lazy(() => import('./admin/pages/Claims'));
const AdminRefunds = lazy(() => import('./admin/pages/Refunds'));

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<StorefrontLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/why-orenyva" element={<WhyOrenyva />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/confirmation/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/account" element={<AccountDashboard />} />
            <Route path="/account/orders" element={<AccountOrders />} />
            <Route path="/account/orders/:id" element={<AccountOrderDetail />} />
            <Route path="/account/orders/:id/tracking" element={<OrderTracking />} />
            <Route path="/account/profile" element={<AccountProfile />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<AdminProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<Dashboard />} />
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
            <Route path="/admin/shipments" element={<AdminShipments />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/products/new" element={<AdminProductEditor />} />
            <Route path="/admin/products/:id" element={<AdminProductEditor />} />
            <Route path="/admin/inventory" element={<AdminInventory />} />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route path="/admin/customers/:id" element={<AdminCustomerDetail />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/coupons" element={<AdminCoupons />} />
            <Route path="/admin/collections" element={<AdminCollections />} />
            <Route path="/admin/content" element={<AdminContent />} />
            <Route path="/admin/media" element={<AdminMedia />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/admin-users" element={<AdminUsers />} />
            <Route path="/admin/activity" element={<AdminActivity />} />
            <Route path="/admin/claims" element={<AdminClaims />} />
            <Route path="/admin/refunds" element={<AdminRefunds />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
